import { X } from "lucide-react";
import { forwardRef } from "react";

import { cn } from "@/lib/utils";

export enum BadgeVariant {
  ORANGE = "orange",
  GREEN = "green",
  BLUE = "blue",
  AMBER = "amber",
  PURPLE = "purple",
  GRAY = "gray",
  RED = "red",
}

export enum BadgeSize {
  XS = "xs",
  SM = "sm",
  MD = "md",
  LG = "lg",
}

interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant: BadgeVariant;
  size?: BadgeSize;
  text?: string | number;
  showDot?: boolean;
  animate?: boolean;
  className?: string;
  onRemove?: () => void;
  onDelete?: () => void;
}

const badgeSizes = {
  xs: "h-4 px-1 text-[0.625rem]",
  sm: "h-5 px-1.5",
  md: "h-6 px-2",
  lg: "h-8 px-2.5",
};

const variantStyles: Record<BadgeVariant, string> = {
  orange: "border-orange-300 from-orange-100 to-orange-200 text-orange-700",
  green: "border-green-300 from-green-100 to-green-200 text-green-700",
  blue: "border-blue-300 from-blue-100 to-blue-200 text-blue-700",
  amber: "border-amber-300 from-amber-100 to-amber-200 text-amber-700",
  purple: "border-purple-300 from-purple-100 to-purple-200 text-purple-700",
  gray: "border-gray-300 from-gray-100 to-gray-200 text-gray-800",
  red: "border-red-300 from-red-100 to-red-200 text-red-700",
};

const dotColors: Record<BadgeVariant, string> = {
  orange: "bg-orange-700",
  green: "bg-green-700",
  blue: "bg-blue-700",
  amber: "bg-amber-700",
  purple: "bg-purple-700",
  gray: "bg-gray-700",
  red: "bg-red-700",
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant,
      size = BadgeSize.MD,
      text,
      className,
      showDot = false,
      animate = false,
      onRemove,
      onDelete,
      children,
      ...props
    },
    ref,
  ) => (
    <span
      ref={ref}
      className={cn(
        `flex w-fit items-center justify-center gap-1 whitespace-nowrap rounded-full border bg-gradient-to-r py-1 text-xs ${variantStyles[variant]} ${badgeSizes[size]}`,
        className,
      )}
      {...props}
    >
      {showDot && (
        <div
          className={`h-2 w-2 rounded-full ${dotColors[variant]} ${animate ? "animate-pulse" : ""}`}
        />
      )}
      {children || <span className="-mb-px text-xs font-semibold leading-none">{text}</span>}
      {(onRemove || onDelete) && (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            (onRemove ?? onDelete)?.();
          }}
          className="ml-0.5 flex h-3 w-3 items-center justify-center rounded-sm transition-opacity hover:opacity-70"
          aria-label="Remove"
        >
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  ),
);

Badge.displayName = "Badge";
